import { useModalMeta } from "@/shared/atoms/useModal";
import useActivityHook from "@/shared/hooks/useActivityHook";
import useCommon from "@/shared/hooks/useCommon";
import useGetBranch from "@/shared/hooks/useGetBranch";
import useIsSuperAdmin from "@/shared/hooks/useIsSuperAdmin";
import useOnboardingMutation from "@/shared/mutations/onboarding/useOnboardingMutation";
import { onboardingStatuses } from "@/shared/utils/constant";
import { formatDate } from "@/shared/utils/helpers";
import { ActivityHistory, OnboardingActivity } from "@/shared/utils/interfaces";
import { useEffect, useMemo, useState } from "react";
import Button from "../Button/Button";
import Textarea from "../Input/Textarea";
import Modal, { ModalProps } from "../Modal/Modal";
import Select from "../Select/Select";
import Table from "../Table/Table";
import { TableColumn } from "../Table/types";

export interface ActivityModalProps extends ModalProps {}

export interface Meta {
  branchId: number;
  activity: OnboardingActivity;
}

const AdminSection = ({ activity }: { activity: OnboardingActivity }) => {
  const t = useCommon();
  const columns = useMemo<TableColumn<ActivityHistory>[]>(() => {
    return [
      {
        disableSort: true,
        Header: t("status"),
        id: "status",
        accessor: "status",
        Cell: (row) =>
          t(onboardingStatuses.find((o) => o.value == row.status).label ?? ""),
      },
      {
        disableSort: true,
        Header: t("opening_hours.message"),
        id: "message",
        accessor: "message",
        Cell: (row) => row.message || "-",
      },

      {
        disableSort: true,
        Header: t("cms_users.ROLE_USER"),
        id: "user",
        accessor: "user",
        Cell: (row) => (row.user === 0 ? t("system") : row.user),
      },
      {
        disableSort: true,
        Header: t("newsletter.date"),
        id: "time_stamp",
        accessor: "time_stamp",
        Cell: (row) => formatDate(row.time_stamp, "dateTime"),
      },
    ];
  }, [t]);
  return (
    <>
      <div className="text-[2.4rem]" style={{ fontWeight: "900" }}>
        {t("history")}
      </div>
      <Table
        disableAction
        disableFilter
        disablePagination
        disableSort
        tableName="adminStatusHistory"
        columns={columns}
        data={activity.activity_history.reverse()}
      />
    </>
  );
};

const ActivityModal = ({ ...props }: ActivityModalProps) => {
  const isSuperAdmin = useIsSuperAdmin();
  const { mutate, isPending } = useOnboardingMutation();
  const [option, setOption] = useState(onboardingStatuses[0]);
  const [message, setMessage] = useState("");
  const [meta] = useModalMeta();
  const t = useCommon();
  const { branchId, activity } = meta as Meta;
  const branch = useGetBranch(branchId);
  const { name } = useActivityHook(activity);

  useEffect(() => {
    if (meta.defaultStatus) {
      setOption(onboardingStatuses.find((o) => o.value === meta.defaultStatus));
    }
  }, [meta.defaultStatus]);

  const onSave = () => {
    mutate({
      status: option.value,
      item_id: activity.item_id,
      message,
      branch_id: branchId,
    });
    props.onExit();
  };

  return (
    <Modal {...props}>
      <div className="text-[2.2rem]" style={{ fontWeight: "900" }}>
        {name}
      </div>
      <div className="mb-[1rem]">{branch.branch.name}</div>
      <Select
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        menuPortalTarget={document.getElementById("root")}
        options={onboardingStatuses.filter((o) => o.value !== 4)}
        getOptionLabel={(option) => t(option.label)}
        value={option}
        onChange={(option) => setOption(option)}
      />
      <div className="mt-6 w-full">
        <Textarea
          width={"100%"}
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("optionalMessage")}
        />
      </div>
      <Button isPending={isPending} onClick={onSave}>
        {t("save")}
      </Button>
      {isSuperAdmin && (
        <>
          <hr className="my-[1rem]" />
          <AdminSection activity={activity} />
        </>
      )}
    </Modal>
  );
};

export default ActivityModal;
